const DEFAULT_ENV_KEY = 'GPT_API_TRANSCRIPT_ANALYSIS_KEY';
const DEFAULT_MODEL = 'gpt-5.5';
const DEFAULT_AGENT_NAME = 'VNS Call Script';
const PHONE_PATTERN = '^(?:Not Provided|\\+[1-9][0-9]{7,14})$';
const EMAIL_PATTERN = '^(?:Not Provided|[^\\s@]+@[^\\s@]+\\.[^\\s@]+)$';
const EMAIL_FIELD_PATTERN = /(^|[^a-z0-9])e-?mail($|[^a-z0-9])/i;

const cleanText = (value) => String(value ?? '')
	.replace(/\r\n?/g, '\n')
	.split('\n')
	.map(line => line.trim())
	.filter(Boolean)
	.join(' ')
	.trim();

const splitOptions = (options) => {
	const values = Array.isArray(options) ? options : [options];
	return [...new Set(values
		.flatMap(value => String(value ?? '').split(/\r?\n/))
		.map(value => value.trim())
		.filter(Boolean))];
};

const getFieldName = (label) => label.replace(/\./g, '_');

const isEmailQuestion = (question) => [question.type, question.label, question.comment]
	.some(value => EMAIL_FIELD_PATTERN.test(value));

const getSchemaName = (agentName) => {
	const schemaName = agentName
		.normalize('NFKD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, 64);

	return schemaName || 'vns_call_script';
};

const escapeSettingValue = (value) => String(value).replace(/: /g, '+:+ ');

const convertEntriesToText = (entries) => entries
	.map(([key, value]) => `${key}: ${escapeSettingValue(value)}`)
	.join('\n');

const normalizeQuestions = (vnsScript) => {
	if (!Array.isArray(vnsScript)) {
		throw new TypeError('convertVnsCallScriptToAiAgent requires a vnsScript array');
	}

	const fieldNames = new Set();

	return vnsScript.map((question, index) => {
		if (!question || typeof question !== 'object' || Array.isArray(question)) {
			throw new TypeError(`vnsScript question ${index + 1} must be an object`);
		}

		const type = cleanText(question.type).toLowerCase() || 'text';
		const label = cleanText(question.label);
		const comment = cleanText(question.comment);

		if (type === 'label') {
			return { type, label, comment };
		}

		if (!label) {
			throw new Error(`vnsScript question ${index + 1} requires a label`);
		}

		const fieldName = getFieldName(label);
		if (fieldNames.has(fieldName)) {
			throw new Error(`vnsScript contains more than one answerable question named "${fieldName}"`);
		}
		fieldNames.add(fieldName);

		const options = type === 'radio' ? splitOptions(question.options) : [];
		if (type === 'radio' && options.length === 0) {
			throw new Error(`Radio question "${label}" requires at least one option`);
		}

		return { type, label, fieldName, comment, options };
	});
};

const buildInstructions = (questions) => {
	const answerableQuestions = questions.filter(question => question.type !== 'label');
	const callFlowNotes = questions
		.filter(question => question.type === 'label')
		.map(question => question.comment || question.label)
		.filter(Boolean);

	const fieldInstructions = answerableQuestions.flatMap((question, index) => {
		const heading = `${index + 1}. ${question.fieldName} (${question.type})`;
		const details = [];

		if (question.fieldName !== question.label) {
			details.push(`   Script label: ${question.label}`);
		}
		if (question.comment) {
			details.push(`   Script prompt: ${question.comment}`);
		}
		if (question.options.length) {
			details.push(`   Allowed values: ${question.options.join(' | ')}`);
		}

		return [heading, ...details];
	});

	const lines = [
		'Convert the provided VNS Firm call record into the completed call-script fields.',
		'',
		'Extraction rules:',
		'- Return only data that matches the JSON schema.',
		'- Populate every schema field and do not add fields.',
		'- Use the script prompts as context, but return the caller answer rather than the prompt text.',
		'- Do not invent details. Use "Not Provided" for unavailable text, textarea, or phone values.',
		'- For radio fields, return exactly one listed option and only when the call record supports it.',
		'- Format phone numbers in E.164 format: a leading + followed by the country code and digits only, for example +11231231234.',
		'- Keep notes factual and concise.',
		'',
		'Output fields:',
		...fieldInstructions
	];

	if (callFlowNotes.length) {
		lines.push(
			'',
			'Call-flow context (do not create output fields for these label-only steps):',
			...callFlowNotes.map((note, index) => `${index + 1}. ${note}`)
		);
	}

	return lines.join('\n').trim();
};

const buildFlatAgentEntries = (questions, options) => {
	const {
		agentName,
		envKey,
		model,
		task,
		doc,
		schemaName
	} = options;
	const answerableQuestions = questions.filter(question => question.type !== 'label');

	const entries = [
		['envKey', envKey],
		['model', model],
		['input.0.role', 'user'],
		['input.0.content.task', task || `Complete the ${agentName} fields from this VNS Firm call record.`],
		['input.0.content.doc', doc],
		['instructions', buildInstructions(questions)],
		['text.format.type', 'json_schema'],
		['text.format.name', schemaName],
		['text.format.strict', true],
		['text.format.schema.$schema', 'http://json-schema.org/draft-07/schema#'],
		['text.format.schema.type', 'object'],
		['text.format.schema.additionalProperties', false]
	];

	answerableQuestions.forEach((question) => {
		const propertyPath = `text.format.schema.properties.${question.fieldName}`;
		entries.push([`${propertyPath}.type`, 'string']);

		if (question.type === 'radio') {
			question.options.forEach((option, index) => {
				entries.push([`${propertyPath}.enum.${index}`, option]);
			});
		}

		if (question.type === 'phone') {
			entries.push([`${propertyPath}.pattern`, PHONE_PATTERN]);
		} else if (isEmailQuestion(question)) {
			entries.push([`${propertyPath}.pattern`, EMAIL_PATTERN]);
		}
	});

	answerableQuestions.forEach((question, index) => {
		entries.push([`text.format.schema.required.${index}`, question.fieldName]);
	});

	return entries;
};

/**
 * Convert a VNS call-script question array into one settings/aiAgents field.
 *
 * @param {Array<object>} vnsScript VNS questions in their stored order.
 * @param {object|string} [options] Conversion options, or the agent name.
 * @param {string} [options.agentName="VNS Call Script"] Firestore field name.
 * @param {string} [options.envKey="GPT_API_TRANSCRIPT_ANALYSIS_KEY"] API-key environment variable.
 * @param {string} [options.model="gpt-5.5"] Responses API model.
 * @param {string} [options.task] User-facing task supplied to the agent.
 * @param {string} [options.doc="[[doc]]"] runAgentRequest placeholder.
 * @param {string} [options.schemaName] JSON Schema response-format name.
 * @returns {object} A merge-ready settings/aiAgents field object. This function does not write it.
 */
export const convertVnsCallScriptToAiAgent = (vnsScript, options = {}) => {
	const normalizedOptions = typeof options === 'string' ? { agentName: options } : options;
	if (!normalizedOptions || typeof normalizedOptions !== 'object' || Array.isArray(normalizedOptions)) {
		throw new TypeError('convertVnsCallScriptToAiAgent options must be an object or agent name');
	}

	const agentName = cleanText(normalizedOptions.agentName) || DEFAULT_AGENT_NAME;
	const envKey = cleanText(normalizedOptions.envKey) || DEFAULT_ENV_KEY;
	const model = cleanText(normalizedOptions.model) || DEFAULT_MODEL;
	const doc = cleanText(normalizedOptions.doc) || '[[doc]]';
	const schemaName = cleanText(normalizedOptions.schemaName) || getSchemaName(agentName);
	const questions = normalizeQuestions(vnsScript);

	if (!questions.some(question => question.type !== 'label')) {
		throw new Error('vnsScript must contain at least one answerable question');
	}

	const entries = buildFlatAgentEntries(questions, {
		...normalizedOptions,
		agentName,
		envKey,
		model,
		doc,
		schemaName
	});

	return {
		[agentName]: {
			value: convertEntriesToText(entries),
			translations: {},
			permission: 'Admin',
			type: 'TextArea'
		}
	};
};
